import React, { useState } from "react";
import "./Share.scss";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { Button } from "../../../components";
import emailIcon from "../../../assets/icon-email.svg";
import whatsappIcon from "../../../assets/icon-whatsapp.svg";
import { emailCustomerStatement } from "../../../api/statement-service/requests";
import IkhokhaLoader from "../../../assets/iK-Load-Animation.gif";
import successIcon from "../../../assets/icon-complete.svg";
import { setNavigateOptions } from "../../../utils/utils";
import { getChannel } from "../../../utils/Jwt";
import { Channel } from "../../../types";

const shareErrorHeader = "Unable to share invoice";
const shareErrorMessage = `Unfortunately, your invoice was not shared. Please try again or contact iKhokha on 087 222 7000 or email us at support@ikhokha.com`;

export interface ShareProps {
  onDone: () => void;
  invoiceId: string;
  invoiceCreateDate: string;
  onShare: () => Promise<any>;
}
const Share = ({ onDone, invoiceId, onShare, invoiceCreateDate }: ShareProps) => {
  const navigate = useNavigate();
  const merchant = `${useSelector(
    // @ts-ignore
    state => state.authenticationReducer.loggedInMerchant,
  )}`;
  const merchantDetails = useSelector(
    // @ts-ignore
    state => state.authenticationReducer.merchantDetails,
  );
  const [loading, setLoading] = useState(false);
  const [showEmailSuccess, setShowEmailSuccess] = useState(false);
  const [confirm, setConfirm] = useState(false);
  const { companyName } = merchantDetails;
  const invoiceUrl = `https://${process.env.REACT_APP_CUSTOM_SUBDOMAIN_NAME}/statement/${invoiceId}`;
  const shareInvoiceText = "Share the invoice with your customer via email or WhatsApp.";
  const shareViaWhatsappText = `${invoiceUrl}\r\n\r\nHi there, \r\n\r\n${companyName} has just sent you an invoice. Please use the link above to view and make payment.`;
  const shareViaWhatsapp = () => {
    onShare()
      .then(() => {
        const channel = getChannel();
        if (channel === Channel.IOS_TOP) {
          const shareViaWhatsappUrl = `https://api.whatsapp.com/send/?text=${encodeURI(shareViaWhatsappText)}`;
          window.location.href = shareViaWhatsappUrl;
        } else {
          const windowReference = window.open("about:blank");
          const shareViaWhatsappUrl = `https://api.whatsapp.com/send/?text=${encodeURI(shareViaWhatsappText)}`;
          if (windowReference) {
            windowReference.location = shareViaWhatsappUrl;
          } else {
            throw new Error("Failed to share via whatsapp");
          }
        }
      })
      .catch(() => {
        navigate(`/error`, {
          state: setNavigateOptions(true, "cnp", shareErrorHeader, shareErrorMessage),
        });
      });
  };
  const shareViaEmail = async () => {
    setConfirm(false);
    setLoading(true);
    const sendEmail = emailCustomerStatement(merchant, invoiceCreateDate, invoiceId);
    Promise.all([await onShare(), await sendEmail])
      .then(values => {
        // todo handle error condition
        if (values[1]) {
          setLoading(false);
          setShowEmailSuccess(true);
        }
      })
      .catch(() => {
        navigate(`/error`, {
          state: setNavigateOptions(true, "cnp", shareErrorHeader, shareErrorMessage),
        });
      });
  };

  return (
    <div className="share-container px-4">
      {!showEmailSuccess && !confirm && (
        <>
          <div className="subtitle">{shareInvoiceText}</div>
          <div>
            <div className={`prompt-container ${loading ? "" : "gray"} p-4`}>
              {!loading && (
                <>
                  <div className="p-1 pb-3">Share the invoice via the options below:</div>
                  <div className="share-button-container pt-2">
                    <button type="button" className="share-icon text-bold" onClick={shareViaEmail}>
                      <img src={emailIcon} alt="Email icon" />
                      Email
                    </button>
                    <button type="button" className="share-icon text-bold" onClick={shareViaWhatsapp}>
                      <img src={whatsappIcon} alt="WhatsApp icon" />
                      WhatsApp
                    </button>
                  </div>
                </>
              )}
              {loading && <img src={IkhokhaLoader} alt="Loading" />}
            </div>
          </div>
        </>
      )}
      {showEmailSuccess && !loading && !confirm && (
        <div className="success-container">
          <img src={successIcon} alt="Success icon" className="success-icon" />
          <p className="success-text">Success</p>
          <p className="text-size-20">Your invoice was successfully sent.</p>
        </div>
      )}
      {!confirm && (
        <div className="action-button pb-4">
          <Button
            disabled={loading}
            label="Done"
            size="medium"
            onClick={() => {
              setConfirm(false);
              onDone();
            }}
          />
        </div>
      )}
    </div>
  );
};

export default Share;
